import { Controller } from '@hotwired/stimulus'
import { i18n } from 'config/translations'
import { programEndDate } from 'utils/plan_duration_calculator'

export default class extends Controller {
  static targets = ['summaryPrice', 'planFee', 'totalFee']

  static values = {
    serviceType: String,
    noAdults: String,
    noNurseryAboard: String,
    noChildren: String,
    noChildrenNoNursery: String,
    noChildrenSchool: String,
    planPrice: String,
    adultStayFee: String,
    preschoolProgramFee: String,
    preschoolStayFee: String,
    primaryProgramFee: String,
    primaryStayFee: String,
    stayDays: String,
    programDays: String,
    // [hrg-ui2] 宿なしプラン（部屋なし / School Fee Only）判定。true のとき予約リンクの end_of_stay を
    // 宿泊数ではなく留学日数(program_days)の平日カウントで求める（バックエンド roomless_plan? と同一基準）。
    roomless: Boolean,
    startDate: String,
    planId: String,
  }

  connect() {
    this.setPeople(
      '2',
      this.serviceTypeValue.includes('保育園留学') ? '1' : '0',
      this.serviceTypeValue.includes('小学生留学') ? '1' : '0',
      '0',
      '0',
    )
    const dateRangePickerController = this.application.getControllerForElementAndIdentifier(this.element, 'date-range-picker')
    if (dateRangePickerController) {
      dateRangePickerController.initialPrice(
        parseInt(this.planPriceValue),
        parseInt(this.adultStayFeeValue),
        parseInt(this.preschoolProgramFeeValue),
        parseInt(this.preschoolStayFeeValue),
        parseInt(this.primaryProgramFeeValue),
        parseInt(this.primaryStayFeeValue),
      )
      // date-range-picker が先に connect して start_date の日を選んでいたら、その save() は料金を取りにいけていない
      dateRangePickerController.retryPendingSave()
    }
    this.calculate()
  }

  setPeople(adultNo, noNurseryAboard, noChildrenNursery, noChildrenNoNursery, noChildrenSchool) {
    this.noAdultsValue = adultNo
    this.noNurseryAboardValue = noNurseryAboard
    this.noChildrenValue = noChildrenNursery
    this.noChildrenNoNurseryValue = noChildrenNoNursery
    this.noChildrenSchoolValue = noChildrenSchool
  }

  setPrice(planPrice, adultStayFee, preschoolProgramFee, preschoolStayFee, primaryProgramFee, primaryStayFee) {
    this.planPriceValue = planPrice
    this.adultStayFeeValue = adultStayFee
    this.preschoolProgramFeeValue = preschoolProgramFee
    this.preschoolStayFeeValue = preschoolStayFee
    this.primaryProgramFeeValue = primaryProgramFee
    this.primaryStayFeeValue = primaryStayFee
  }

  setProgramDays(programDays) {
    this.programDaysValue = programDays
  }

  setStartDate(startDate) {
    this.startDateValue = startDate
  }

  formatDate(date) {
    const d = new Date(date)
    let month = '' + (d.getMonth() + 1)
    let day = '' + d.getDate()
    const year = d.getFullYear()

    if (month.length < 2) {
      month = '0' + month
    }
    if (day.length < 2) {
      day = '0' + day
    }

    return [year, month, day].join('-')
  }

  clicked() {
    const dateRangePickerController = this.application.getControllerForElementAndIdentifier(this.element, 'date-range-picker')
    let startDate = ''
    if (dateRangePickerController) {
      startDate = dateRangePickerController.getSelectedDate()
    }
    if (!startDate) {
      alert(i18n.t('message.error.input.select', { field_name: i18n.t('message.error.field.date') }))
      return
    }
    const peopleSelectionController = this.application.getControllerForElementAndIdentifier(this.element, 'people-selection')
    let noNurseryAboard = ''
    let noChildrenNursery = ''
    let noChildrenNoNursery = ''
    let noChildrenSchool = ''
    if (dateRangePickerController) {
      noNurseryAboard = peopleSelectionController.getNoNurseryAboard()
      noChildrenNursery = peopleSelectionController.getNoChildrenNursery()
      noChildrenNoNursery = peopleSelectionController.getNoChildrenNoNursery()
      noChildrenSchool = peopleSelectionController.getNoChildrenSchool()
    }
    // 'YYYY-MM-DD' を new Date() に渡すと UTC として読まれ、UTC より西のタイムゾーンでは日付も曜日もずれる（#2416）
    const [year, month, day] = startDate.split('-').map(Number)
    // [hrg-ui2] 宿なしプランは stay_day=0 のため end_of_stay が start_of_stay と同日になっていた。
    // 留学日数(program_days)の平日カウントで求めた終了日を渡す（date_range_picker の帯・表示と整合）。
    const endDate = this.roomlessValue
      ? programEndDate(new Date(year, month - 1, day), this.programDaysValue)
      : new Date(year, month - 1, day + parseInt(this.stayDaysValue))
    const link =
      '/reservations/new?plan_id=' +
      this.planIdValue +
      '&number_of_adults=' +
      this.noAdultsValue +
      '&number_of_preschoolers=' +
      noNurseryAboard +
      '&number_of_students=' +
      noChildrenNursery +
      '&number_of_preschoolers_1=' +
      noChildrenNoNursery +
      '&number_of_students_1=' +
      noChildrenSchool +
      '&start_of_stay=' +
      startDate +
      '&end_of_stay=' +
      this.formatDate(endDate)
    window.location.href = link
  }

  calculate() {
    const stayNights = parseInt(this.stayDaysValue) || 0
    const programDays = parseInt(this.programDaysValue) || stayNights
    let total =
      parseInt(this.planPriceValue) +
      parseInt(this.noAdultsValue) * parseInt(this.adultStayFeeValue) * stayNights +
      parseInt(this.noNurseryAboardValue) *
        (parseInt(this.preschoolProgramFeeValue) * programDays + parseInt(this.preschoolStayFeeValue) * stayNights) +
      parseInt(this.noChildrenValue) *
        (parseInt(this.primaryProgramFeeValue) * programDays + parseInt(this.primaryStayFeeValue) * stayNights) +
      parseInt(this.noChildrenNoNurseryValue) * parseInt(this.preschoolStayFeeValue) * stayNights +
      parseInt(this.noChildrenSchoolValue) * parseInt(this.primaryStayFeeValue) * stayNights
    total = total.toLocaleString('en-US')
    this.summaryPriceTarget.textContent = i18n.t('price_text', { price: total })
    this.planFeeTarget.textContent = i18n.t('price_text', { price: total })
    this.totalFeeTarget.textContent = i18n.t('price_text', { price: total })
  }
}
